import React, { useState, useRef, useEffect, useContext } from "react";
import ReactDOM from "react-dom";
import { AttendeeContext } from "../context/AttendeeContext";
import { QAContext } from "../context/QAContext";
import TimeoutErrorPage from "../Intake/Pages/TimeoutErrorPage/TimeoutErrorPage";
import { AdvancedLoggingHelper } from "../Utilities/AdvancedLoggingHelper";
import { ARSHelper } from "../Utilities/ARSHelper";
import { MobilePresenterStreamHelper } from "../Utilities/MobilePresenterStreamHelper";
import { PresenterHelper } from "../Utilities/PresenterHelper";
import { ServerHelper } from "../Utilities/ServerHelper";
import { StringVariableHelper } from "../Utilities/StringVariableHelper";
import { TestHelper } from "../Utilities/TestHelper";
import { TwoDHelper } from "../Utilities/TwoDHelper";
import { UIHelper } from "../Utilities/UIHelper";
import { UserPermissionsHelper } from "../Utilities/UserPermissionsHelper";

//Function for setting CSS rules
function setStyleRule(selector, rule, sheetName) {
  var sheets = document.styleSheets,
    stylesheet = sheets[sheets.length - 1];

  for (var i in document.styleSheets) {
    if (sheets[i].href && sheets[i].href.indexOf(sheetName + ".css") > -1) {
      stylesheet = sheets[i];
      break;
    }
  }

  if ((stylesheet as any).addRule) (stylesheet as any).addRule(selector, rule);
  else if ((stylesheet as any).insertRule)
    (stylesheet as any).insertRule(
      selector + " { " + rule + " }",
      (stylesheet as any).cssRules.length
    );
}

const useLoadApp = () => {
  const { handleGetAllQuestions, setAttendeesSeeAllQs, setAllowAnonymousQs } =
    useContext(QAContext);

  const { setPreviousNonBackstageRoomID } = useContext(AttendeeContext);

  const [isIntakeCompleted, toggleIsIntakeCompleted] = useState(
    ServerHelper.useUIDevelopment ? true : false
  );
  const [isJoinCompleted, toggleIsJoinCompleted] = useState(
    ServerHelper.useUIDevelopment ? true : false
  );

  const isDroneModeActive = useRef(false);

  const loginPromiseResolver = useRef(null);

  useEffect(() => {
    return function cleanup() {
      document.removeEventListener("keypress", handleDroneKeyPress);
    };
  }, []);

  const handleDroneKeyPress = (e) => {
    if (e.key === "=") {
      //Check if drone mode is allowed
      if (
        (window as any).allowDroneMode !== undefined &&
        (window as any).allowDroneMode === true
      ) {
        if (isDroneModeActive.current === false) {
          setStyleRule("*", "cursor: none !important", "App.css");

          isDroneModeActive.current = true;
        } else {
          setStyleRule("*", "cursor: auto !important", "App.css");

          isDroneModeActive.current = false;
        }
      }
    }
  };

  const handleWebsocketLoginSuccess = async (
    response: SHOWBOAT.LoginReturnData
  ) => {
    SHOWBOAT.WebSocketController.OnLocalPlayerLoggedIn.Remove(
      handleWebsocketLoginSuccess
    );
    
    SHOWBOAT.Logger.Log("Login response", response);

    //Store the user ID
    SHOWBOAT.LocalAvatarDataManager.userID = response.userID;

    //TODO: TODO: TODO: Store the user's role
    SHOWBOAT.LocalAvatarDataManager.role = response.role;

    //Ensure join succeeded
    if (!response.succeeded) {
      SHOWBOAT.Logger.Error(
        "Error. Unable to login to Showboat application server"
      );

      let errorCopy: string;

      //TODO: Check if join fail is result of event being full
      /* if (response.failReason === "Event full") {
        errorCopy = "Event is currently at maximum capacity.";
      } else { */
      errorCopy = "Error. Unable to login to Showboat application server.";
      /*  } */

      UIHelper.SendErrorLog(errorCopy);

      ReactDOM.render(
        <TimeoutErrorPage failCopy={errorCopy} />,
        document.getElementById("root")
      );
      return;
    }

    //Store our room ID
    SHOWBOAT.LocalAvatarDataManager.roomID = response.roomID;
    setPreviousNonBackstageRoomID(response.roomID);

    //TODO: Get the initial server variables
    let bookingVariablesData: SHOWBOAT.GetAllGroupVariablesReturnData;
    let roomVariablesData: SHOWBOAT.GetAllGroupVariablesReturnData;

    try {
      bookingVariablesData =
        await SHOWBOAT.WebSocketController.GetAllGroupVariables({
          groupType: SHOWBOAT.GroupTypes.booking,
        });
      
      SHOWBOAT.Logger.Debug("Booking variables data", bookingVariablesData);

      if (bookingVariablesData.errorData.error) {
        throw new Error(bookingVariablesData.errorData.errorMessage);
      }

      roomVariablesData =
        await SHOWBOAT.WebSocketController.GetAllGroupVariables({
          groupType: SHOWBOAT.GroupTypes.room,
        });

      SHOWBOAT.Logger.Debug("Room variables data", roomVariablesData);

      if (roomVariablesData.errorData.error) {
        throw new Error(roomVariablesData.errorData.errorMessage);
      }

      if (!bookingVariablesData || !roomVariablesData) {
        throw new Error("Error retrieving variables");
      }
    } catch (error) {
      SHOWBOAT.Logger.Error("Error retrieving event variables");

      SHOWBOAT.Logger.Debug(error);

      UIHelper.SendErrorLog("Error retrieving event variables");

      ReactDOM.render(
        <TimeoutErrorPage
          failCopy={"Error logging in to Showboat application server"}
        />,
        document.getElementById("root")
      );
      return;
    }

    //Use retrieved variablesData and roomVariablesData to Init VariableManager
    await SHOWBOAT.ServerVariableManager.Init(
      SHOWBOAT.LocalAvatarDataManager.roomID,
      roomVariablesData.values,
      bookingVariablesData.values,
    );

    let systemInformationDebugInfo =
      SHOWBOAT.SystemInformation.getDebugString();
    SHOWBOAT.Logger.Server("System Information:", systemInformationDebugInfo);

    let babylonDebugData = SHOWBOAT.ShowboatLoader.getEngineDebugInfo();
    if (babylonDebugData) {
      babylonDebugData.forEach((element) => {
        SHOWBOAT.Logger.Server(element);
      });
    }

    //Get the userID
    /*
    let userID: string = ""
    try {
      userID = await SHOWBOAT.SocketIOController.GetUserID(); 
    } catch (err) {
      SHOWBOAT.Logger.Error("Error obtaining user ID for Showboat application server.");
      SHOWBOAT.Logger.Error(err);

      ReactDOM.render(
        <TimeoutErrorPage failCopy="Error obtaining user ID for Showboat application server." />,
        document.getElementById('root')
      );
      return;
    }
    

    //Store the user ID
    SHOWBOAT.LocalAvatarDataManager.userID = userID;
    */

    //If on mobile, initialize the mobile presenter stream helper
    if (ServerHelper.useMobileApp) {
      MobilePresenterStreamHelper.Init();
    }

    //Initialize the Presenter Helper
    PresenterHelper.Init();

    //Initialize the Test Helper
    TestHelper.Init();

    //Initialize the User Permissions Helper
    UserPermissionsHelper.Init();

    //Allow page to do post join work
    toggleIsJoinCompleted(true);

    //Setup liveswitch
    if (!ServerHelper.useMobileHybridApp) {
      //No need to start a liveswitch client if we are a mobile hybrid user

      SHOWBOAT.LiveSwitchClientController.createClient(
        ServerHelper.VideoServerAppID,
        SHOWBOAT.LocalAvatarDataManager.userID,
        SHOWBOAT.LiveSwitchClientController.generateUUID(),
        "ShowboatWebClient_" + SHOWBOAT.LocalAvatarDataManager.eventID,
        ServerHelper.VideoServer
      );

      let registerResult: boolean = false;
      try {
        registerResult = await SHOWBOAT.LiveSwitchClientController.register();
      } catch (err) {
        SHOWBOAT.Logger.Error(
          "Exception error registering with Showboat video server."
        );

        UIHelper.SendErrorLog(
          "Exception error registering with Showboat video server."
        );

        ReactDOM.render(
          <TimeoutErrorPage failCopy="Exception error registering with Showboat video server." />,
          document.getElementById("root")
        );
        return;
      }

      //Check registration was a success
      if (!registerResult) {
        SHOWBOAT.Logger.Error("Error registering with Showboat video server.");

        UIHelper.SendErrorLog("Error registering with Showboat video server.");

        ReactDOM.render(
          <TimeoutErrorPage failCopy="Error registering with Showboat video server." />,
          document.getElementById("root")
        );
        return;
      }
    }

    //Prep out 2D helper
    await TwoDHelper.Init();

    //Prep jumbotron helper
    if (ServerHelper.useMobileApp) {
      SHOWBOAT.JumbotronHelper.Init(ServerHelper.useMobileApp);
    }

    //Start my video stream as an attendees
    let sendUpstream: boolean = true;
    if (
      SHOWBOAT.RemotePlayersZoneConfig.doAvatarSimulation &&
      !SHOWBOAT.RemotePlayersZoneConfig.doStreamSimulatedVideo
    ) {
      sendUpstream = false;
    }

    if (sendUpstream && !ServerHelper.useMobileApp) {
      SHOWBOAT.LiveswitchUpstreamController.Init(
        SHOWBOAT.LocalAvatarDataManager.userID,
        SHOWBOAT.ShowboatChannelType.Attendees,
        SHOWBOAT.LocalAvatarDataManager.avatarData.role,
        SHOWBOAT.LocalAvatarDataManager.eventID,
        response.roomID
      );
    }

    //PRE-WARM a media stream
    if (!ServerHelper.useMobileHybridApp) {
      try {
        await SHOWBOAT.LiveswitchMediaDownstreamController.Start(
          SHOWBOAT.LocalAvatarDataManager.eventID
        );

        /*     if (ServerHelper.useMobileApp) {
          try {
            SHOWBOAT.Logger.Log("Playing initial mobile video");
            SHOWBOAT.LiveswitchMediaDownstreamController.getHTMLVideoElement().setAttribute("playsinline", "");
            await SHOWBOAT.LiveswitchMediaDownstreamController.getHTMLVideoElement().play();
          } catch (error) {
            SHOWBOAT.Logger.Error("Error playing initial mobile video:", error);
          }
        } */

        //await SHOWBOAT.LiveswitchMediaDownstreamController.Pause();
      } catch (e) {
        SHOWBOAT.Logger.Error("Error setting up media downstream");
        SHOWBOAT.Logger.Error(e);
      }
    }

    //Get initial presenter variables
    await SHOWBOAT.ServerVariableManager.getBookingVariable(
      StringVariableHelper.LocalVideoVolumeEventName,
      { volume: 1 }
    );

    await SHOWBOAT.ServerVariableManager.getBookingVariable(
      StringVariableHelper.PresentationEventName,
      { mode: SHOWBOAT.JumbotronMode.off, slide: "", userID: "" }
    );
    await SHOWBOAT.ServerVariableManager.getBookingVariable(
      StringVariableHelper.SilenceAudienceEventName,
      { muted: false }
    );
    await SHOWBOAT.ServerVariableManager.getBookingVariable(
      StringVariableHelper.SpatialAudioEventName,
      { spatialAudio: true }
    );

    //Get initial QA values
    let attendeesSeeAllQs =
      await SHOWBOAT.ServerVariableManager.getBookingVariable(
        StringVariableHelper.AttendeesSeeAllQuestionsName,
        { value: true }
      );
    setAttendeesSeeAllQs(attendeesSeeAllQs.data.value);

    let allowAnonymousQs =
      await SHOWBOAT.ServerVariableManager.getBookingVariable(
        StringVariableHelper.AllowAnonymousQuestionsName,
        { value: true }
      );
    setAllowAnonymousQs(allowAnonymousQs.data.value);

    //Prepare the audio distance for the room
    let audioDistanceEventVariable =
      await SHOWBOAT.ServerVariableManager.getBookingVariable(
        StringVariableHelper.AudioDistanceEventName,
        { auto: true, distance: 20, userID: "none" }
      );

    if (
      audioDistanceEventVariable &&
      audioDistanceEventVariable.data.auto !== undefined &&
      audioDistanceEventVariable.data.distance
    ) {
      SHOWBOAT.RemotePlayersZoneConfig.useAutoBubble =
        audioDistanceEventVariable.data.auto;
      if (!SHOWBOAT.RemotePlayersZoneConfig.useAutoBubble) {
        SHOWBOAT.RemotePlayersZoneConfig.setAudioBubbleDistance(
          audioDistanceEventVariable.data.distance
        );
      }
    }

    //Get all existing player data
    let avatarDataArray: SHOWBOAT.AvatarData[] = [];
    try {
      let requestAllPlayerDataResponse =
        await SHOWBOAT.RemoteAvatarDataManager.RequestAllPlayerData();

      

      if (!requestAllPlayerDataResponse) {
        throw new Error("Error requesting player data")
      }

      avatarDataArray = requestAllPlayerDataResponse as SHOWBOAT.AvatarData[];
    } catch (err) {
      SHOWBOAT.Logger.Error(
        "Exception error retrieving participant data from Showboat application server."
      );

      UIHelper.SendErrorLog(
        "Exception error retrieving participant data from Showboat application server."
      );

      ReactDOM.render(
        <TimeoutErrorPage failCopy="Exception error retrieving participant data from Showboat application server." />,
        document.getElementById("root")
      );
      return;
    }

    //Get QA items
    const dataGetAllQuestions = await handleGetAllQuestions();

    SHOWBOAT.Logger.Log("QUESTION HISTORY:", dataGetAllQuestions);

    SHOWBOAT.RemoteAvatarDataManager.setInitialAvatarData(avatarDataArray);

    //Raise event so AttendeeList knows to load initial event list
    SHOWBOAT.UIEventManager.OnSuccessfulRequestAllPlayerData.Raise();

    //Start the liveswitch downstream if not on mobile hybrid app
    if (!ServerHelper.useMobileHybridApp) {
      try {
        await SHOWBOAT.LiveswitchDownstreamController.init(
          SHOWBOAT.LocalAvatarDataManager.eventID,
          response.roomID,
          SHOWBOAT.LocalAvatarDataManager.avatarData
        );
      } catch (err) {
        SHOWBOAT.Logger.Error(
          "Exception error initializing Showboat video server."
        );

        UIHelper.SendErrorLog(
          "Exception error initializing Showboat video server."
        );

        ReactDOM.render(
          <TimeoutErrorPage failCopy="Exception error initializing Showboat video server." />,
          document.getElementById("root")
        );
        return;
      }
    }

    //Initialize the AdvancedLogging helper
    AdvancedLoggingHelper.Init();

    //Prep Audio Engine
    //SHOWBOAT.PlayerAudioController.init();

    let loadStartTime: number = Date.now();

    const calculateLoadTime = () => {
      let duration: number = Date.now() - loadStartTime;
      SHOWBOAT.Logger.Server("Scene load time:", duration);
      SHOWBOAT.ShowboatLoader.OnHideLoadScreen.Remove(calculateLoadTime);
    };

    SHOWBOAT.ShowboatLoader.OnHideLoadScreen.Add(calculateLoadTime);

    //Add listener for camera drone mode
    document.addEventListener("keypress", handleDroneKeyPress);

    //Only load canvas if not on mobile
    if (!ServerHelper.useMobileApp) {
      let sceneSupplement = SHOWBOAT.ApplicationSkin.sceneSupplement
        ? "./scenes/" + SHOWBOAT.ApplicationSkin.sceneSupplement
        : null;

      await SHOWBOAT.ShowboatLoader.Load(
        "showboatCanvas",
        "./scenes/" + SHOWBOAT.ApplicationSkin.mainScene,
        "./scenes/" + SHOWBOAT.ApplicationSkin.mainSceneConfig,
        sceneSupplement,
        {},
        true
      );
    }

    //Send entry notification if enabled for this booking and user is an attendee
    if (
      ServerHelper.loginResult.sendEntryNotifications &&
      SHOWBOAT.LocalAvatarDataManager.role ===
        StringVariableHelper.ShowboatRoles.attendees
    ) {
      const dataSendChatNotification = await UIHelper.sendChatNotification(
        "entry"
      );

      SHOWBOAT.Logger.Log(
        "Chat notification send response:",
        dataSendChatNotification
      );
    }

    //Only init JumbotronHelper here if bypass is not occurring
    if (!ServerHelper.bypass && !ServerHelper.useMobileApp) {
      SHOWBOAT.JumbotronHelper.Init(ServerHelper.useMobileApp);
    }

    if (ServerHelper.useMobileApp) {
      SHOWBOAT.UIEventManager.OnAudioUnlockEvent.Raise();
    }

    loginPromiseResolver.current();

    //TESTING!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
    //SHOWBOAT.JumbotronHelper.Init();
    //JumbotronHelper.Init();
  };

  const loadApp = async () => {
    //toggle intake completed
    toggleIsIntakeCompleted(true);

    //Connect to the socket
    let connectionResult: boolean = false;
    try {
      SHOWBOAT.Logger.Log("Attempting to connect");
      connectionResult = await ServerHelper.Connect(true);
      SHOWBOAT.Logger.Log("Connection result:", connectionResult);
    } catch (err) {
      SHOWBOAT.Logger.Log(
        "Exception error connecting to showboat websocket server event"
      );
      SHOWBOAT.Logger.Error(err);

      UIHelper.SendErrorLog(
        "Exception error connecting to Showboat application server."
      );

      ReactDOM.render(
        <TimeoutErrorPage failCopy="Exception error connecting to Showboat application server." />,
        document.getElementById("root")
      );

      return;
    }

    //Ensure we got a valid connection
    if (!connectionResult) {
      SHOWBOAT.Logger.Error("Error connecting to Showboat application server.");

      UIHelper.SendErrorLog("Error connecting to Showboat application server.");

      ReactDOM.render(
        <TimeoutErrorPage failCopy="Error connecting to Showboat application server." />,
        document.getElementById("root")
      );

      return;
    }

    //Signal Server Helper to start monitoring debug events
    await ServerHelper.ManageDeviceDebugInfo();

    //Join the event
    try {
      //Check if we are using the simulation testing version of app
      if (SHOWBOAT.RemotePlayersZoneConfig.doAvatarSimulation) {
        if (SHOWBOAT.RemotePlayersZoneConfig.doStreamSimulatedVideo) {
          SHOWBOAT.LocalAvatarDataManager.cameraEnabled = true;
          SHOWBOAT.LocalAvatarDataManager.micEnabled =
            SHOWBOAT.RemotePlayersZoneConfig.doStreamSimulatedAudio;
        } else {
          SHOWBOAT.LocalAvatarDataManager.cameraEnabled = false;
          SHOWBOAT.LocalAvatarDataManager.micEnabled = false;
          SHOWBOAT.LocalAvatarDataManager.currentCameraName = "BOT";
        }

        SHOWBOAT.LocalAvatarDataManager.firstName =
          ServerHelper.getRandomFirst();
        SHOWBOAT.LocalAvatarDataManager.lastName = ServerHelper.getRandomLast();
        SHOWBOAT.LocalAvatarDataManager.company =
          (Math.random() < 0.7
            ? ServerHelper.getRandomLast()
            : ServerHelper.getRandomLast() +
              " & " +
              ServerHelper.getRandomLast()) +
          " " +
          ServerHelper.getRandomCompanySuffix();
        SHOWBOAT.LocalAvatarDataManager.face = Math.floor(Math.random() * 6);
        SHOWBOAT.LocalAvatarDataManager.color = Math.floor(Math.random() * 6);
      }

      //Set system information
      SHOWBOAT.LocalAvatarDataManager.browser = `${SHOWBOAT.SystemInformation.BrowserName} ${SHOWBOAT.SystemInformation.BrowserVersion}`;
      SHOWBOAT.LocalAvatarDataManager.operatingSystem = `${SHOWBOAT.SystemInformation.OSName} ${SHOWBOAT.SystemInformation.OSVersion}`;
      SHOWBOAT.LocalAvatarDataManager.graphicsDriver =
        SHOWBOAT.ShowboatLoader.getGraphicsDrive();
      SHOWBOAT.LocalAvatarDataManager.isMobileUser = ServerHelper.useMobileApp;
      SHOWBOAT.LocalAvatarDataManager.isAvailableForTeleport =
        !SHOWBOAT.LocalAvatarDataManager.isMobileUser;

      let serverToken = await ServerHelper.getServerToken();

      if (!serverToken) {
        ReactDOM.render(
          <TimeoutErrorPage failCopy="Exception error logging into Showboat application server." />,
          document.getElementById("root")
        );

        return;
      }

      SHOWBOAT.Logger.Log("Calling to login:", SHOWBOAT.LocalAvatarDataManager.avatarData, serverToken);

      

      const loginPromise = new Promise((resolve) => {
        SHOWBOAT.WebSocketController.Login({
          loginUserData: SHOWBOAT.LocalAvatarDataManager.avatarData,
          jwt: serverToken,
        } as SHOWBOAT.LoginUpData);
        
        //Store resolver 
        loginPromiseResolver.current = resolve;
      })

      //Client must now wait for OnLocalPlayerLoggedIn to raise to signify login flow is complete
      SHOWBOAT.WebSocketController.OnLocalPlayerLoggedIn.Add(
        handleWebsocketLoginSuccess
      );

      await loginPromise;
    } catch (err) {
      SHOWBOAT.Logger.Error(
        "Exception error logging into Showboat application server",
        err
      );

      UIHelper.SendErrorLog(
        "Exception error logging into Showboat application server."
      );

      ReactDOM.render(
        <TimeoutErrorPage failCopy="Exception error logging into Showboat application server." />,
        document.getElementById("root")
      );

      return;
    }
  };

  return {
    isIntakeCompleted,
    isJoinCompleted,
    loadApp,
    toggleIsIntakeCompleted,
  };
};

export default useLoadApp;
